import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, Clock, Calendar, AlertCircle } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

interface ViewSubmittedSelfieDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventTitle: string;
}

export const ViewSubmittedSelfieDialog = ({
  open,
  onOpenChange,
  eventId,
  eventTitle,
}: ViewSubmittedSelfieDialogProps) => {
  const { user } = useAuth();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  // Fetch the submitted selfie for this event
  const { data: selfieData, isLoading } = useQuery({
    queryKey: ["submitted-selfie", eventId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("user_photos")
        .select("id, file_path, caption, created_at, status")
        .eq("user_id", user.id)
        .eq("related_event_id", eventId)
        .eq("is_checkin_selfie", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[ViewSubmittedSelfie] Error fetching selfie:", error);
        return null;
      }

      return data;
    },
    enabled: open && !!user?.id && !!eventId,
  });

  // Get signed URL when we have the file path
  useEffect(() => {
    const getSignedUrl = async () => {
      if (!selfieData?.file_path) return;

      const { data, error } = await supabase.storage
        .from("private-user-media")
        .createSignedUrl(selfieData.file_path, 3600);

      if (error) {
        console.error("[ViewSubmittedSelfie] Signed URL error:", error);
        setImageError(true);
      } else if (data) {
        setPhotoUrl(data.signedUrl);
      }
    };

    if (selfieData?.file_path) {
      setPhotoUrl(null);
      setImageError(false);
      getSignedUrl();
    }
  }, [selfieData?.file_path]);

  const getStatusBadge = () => {
    switch (selfieData?.status) {
      case "pending":
        return (
          <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
            <Clock className="w-3 h-3 mr-1" />
            Under Review
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="secondary" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
            ✓ Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive">
            Not Accepted
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Camera className="w-5 h-5" />
            Your Check-In Selfie
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Event Info */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="font-medium text-sm text-foreground line-clamp-2">{eventTitle}</p>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="w-full aspect-square rounded-lg" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {/* No Selfie Found */}
          {!isLoading && !selfieData && (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No selfie found for this event</p>
            </div>
          )}

          {/* Selfie Display */}
          {!isLoading && selfieData && (
            <div className="space-y-3">
              {/* Status Badge */}
              <div className="flex justify-center">
                {getStatusBadge()}
              </div>

              {/* Photo */}
              <div className="relative rounded-lg overflow-hidden bg-muted">
                {photoUrl && !imageError ? (
                  <img
                    src={photoUrl}
                    alt="Your check-in selfie"
                    className="w-full aspect-square object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : imageError ? (
                  <div className="w-full aspect-square flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">Image unavailable</p>
                    </div>
                  </div>
                ) : (
                  <Skeleton className="w-full aspect-square" />
                )}
              </div>

              {/* Caption */}
              {selfieData.caption && (
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-sm text-foreground">{selfieData.caption}</p>
                </div>
              )}

              {/* Submission Time */}
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                <span>
                  Submitted {formatDistanceToNow(new Date(selfieData.created_at), { addSuffix: true })}
                </span>
              </div>

              {/* Review Status Info */}
              {selfieData.status === "pending" && (
                <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Your selfie is being reviewed by our team. You'll get a notification once it's approved!
                  </p>
                </div>
              )}
            </div>
          )}

          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
